import { useState, useEffect } from 'react';
import { SmsIntegrationService } from '../api/services/SmsIntegrationService';

interface SmsProviderStatus {
  isConnected: boolean;
  isLoading: boolean;
}

let cachedStatus: boolean | null = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 5 * 60 * 1000;
let inflightPromise: Promise<boolean> | null = null;

function fetchStatus(): Promise<boolean> {
  if (!inflightPromise) {
    inflightPromise = SmsIntegrationService.getApiAppSmsIntegrationGetSettings()
      .then((settings) => {
        cachedStatus = settings.isConnected ?? false;
        cacheTimestamp = Date.now();
        return cachedStatus;
      })
      .catch(() => {
        cachedStatus = false;
        cacheTimestamp = Date.now();
        return false;
      })
      .finally(() => {
        inflightPromise = null;
      });
  }
  return inflightPromise;
}

export function invalidateSmsProviderCache() {
  cachedStatus = null;
  cacheTimestamp = 0;
}

export function useSmsProviderStatus(): SmsProviderStatus {
  const isCacheValid = cachedStatus !== null && Date.now() - cacheTimestamp < CACHE_TTL_MS;

  const [isConnected, setIsConnected] = useState<boolean>(isCacheValid ? cachedStatus! : false);
  const [isLoading, setIsLoading] = useState(!isCacheValid);

  useEffect(() => {
    if (isCacheValid) {
      setIsConnected(cachedStatus!);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    fetchStatus().then((connected) => {
      if (!cancelled) {
        setIsConnected(connected);
        setIsLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return { isConnected, isLoading };
}
